import { useState, useEffect, useCallback } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import { useSnackbar } from 'notistack';

// Data-fetching + pagination + delete-mutation wiring shared by the simple admin
// CRUD lists (Group/Period/Place/Grade...): a name-filtered, paginated GraphQL
// list plus a delete mutation with a success/error snackbar.
export function useEntityList({
    listQuery,
    listDataKey,
    deleteMutation,
    deleteVariableName,
    deleteErrorMessage,
    deleteSuccessMessage,
    initialLimit = 10,
}) {
    const { enqueueSnackbar } = useSnackbar();

    const [items, setItems] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [limit, setLimit] = useState(initialLimit);
    const [offset, setOffset] = useState(0);
    const [nameFilter, setNameFilter] = useState("");

    const [getItems] = useLazyQuery(listQuery, {
        onCompleted: (data) => {
            setItems(data[listDataKey].nodes);
            setTotalCount(data[listDataKey].totalCount);
        },
        fetchPolicy: 'cache-and-network'
    });

    const [deleteItem] = useMutation(deleteMutation, {
        onError: () => enqueueSnackbar(deleteErrorMessage, { variant: 'error' }),
        onCompleted: () => enqueueSnackbar(deleteSuccessMessage)
    });

    const updateSearch = (event) => {
        setNameFilter(event.target.value);
    };

    const loadData = useCallback(() => {
        getItems({
            variables: {
                filter: { name: { likeInsensitive: `%${nameFilter}%` } },
                first: limit,
                offset
            }
        });
    }, [nameFilter, getItems, limit, offset]);

    const handleDelete = useCallback(async (uid) => {
        await deleteItem({ variables: { [deleteVariableName]: uid } });
        loadData();
    }, [deleteItem, loadData, deleteVariableName]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    return { items, totalCount, limit, setLimit, offset, setOffset, updateSearch, loadData, handleDelete };
}
